import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Tag, Button, Space, Typography } from 'antd';
import { EyeOutlined, FileTextOutlined, UserOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { getEmployees } from '../../api/employees';
import { getEmployeeDocuments } from '../../api/documents';
import { Employee } from '../../types/resources/employee';

import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;

interface EmployeeDocumentsTabProps {
  onViewDocuments: (employeeId: string, employeeName: string, ownerType: 'employee') => void;
}

const EmployeeDocumentsTab: React.FC<EmployeeDocumentsTabProps> = ({ onViewDocuments }) => {
  const { t } = useTranslation();
  const { firestoreUser } = useAuth();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Fetch employees data
  const { data: employeesData, isLoading: employeesLoading } = useQuery({
    queryKey: ['employees', pagination.current, pagination.pageSize, firestoreUser?.workspace?.id],
    queryFn: () => getEmployees({
      page: pagination.current,
      pageSize: pagination.pageSize,
      firestoreUser: firestoreUser!,
    }),
    enabled: !!firestoreUser?.workspace?.id,
  });

  // Fetch employee documents data
  const { data: documentsData, isLoading: documentsLoading } = useQuery({
    queryKey: ['employeeDocuments', firestoreUser?.workspace?.id],
    queryFn: () => getEmployeeDocuments(firestoreUser?.workspace?.id || ''),
    enabled: !!firestoreUser?.workspace?.id,
  });

  // Create a map of employee documents count
  const documentsCountMap = useMemo(() => {
    if (!documentsData?.data) return new Map();
    
    const countMap = new Map<string, number>();
    documentsData.data.forEach((doc: any) => {
      if (doc.employeeId) {
        const count = countMap.get(doc.employeeId) || 0;
        countMap.set(doc.employeeId, count + 1);
      }
    });
    
    return countMap;
  }, [documentsData]);

  const columns = useMemo(() => [
    {
      title: t('employees:table.name'),
      dataIndex: 'user',
      key: 'name',
      render: (user: any) => (
        <Space direction="vertical" size="small">
          <Space>
            <UserOutlined style={{ color: '#1890ff' }} />
            <Text strong>{user?.displayName || 'N/A'}</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {user?.email || 'N/A'}
          </Text>
        </Space>
      ),
    },
    {
      title: t('employees:table.department'),
      dataIndex: 'department',
      key: 'department',
      render: (department: string) => (
        <Tag color="blue">{department}</Tag>
      ),
    },
    {
      title: t('employees:table.role'),
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color="green">{role}</Tag>
      ),
    },
    {
      title: t('employees:table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {t(`employees:status.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('documents:table.documents'),
      key: 'documents',
      render: (_: any, record: Employee) => {
        const documentCount = documentsCountMap.get(record.id) || 0;
        return (
          <Space>
            <FileTextOutlined style={{ color: '#1890ff' }} />
            <Text>{documentCount} {t('documents:table.document', { count: documentCount })}</Text>
          </Space>
        );
      },
    },
    {
      title: t('employees:table.actions'),
      key: 'actions',
      render: (_: any, record: Employee) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => onViewDocuments(record.id, record.user?.displayName || 'N/A', 'employee')}
          size="small"
        >
          {t('documents:actions.viewDocuments')}
        </Button>
      ),
    },
  ], [t, documentsCountMap]);

  const handleTableChange = (pagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    }));
  };

  return (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <Title level={4}>{t('documents:tabs.employees')}</Title>
        <Text type="secondary">
          {t('documents:tabs.descriptions.employees')}
        </Text>
      </div>
      
      <Table
        columns={columns}
        dataSource={employeesData?.employees || []}
        loading={employeesLoading || documentsLoading}
        rowKey="id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: employeesData?.total || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            t('common:pagination.showing', { from: range[0], to: range[1], total }),
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
};

export default EmployeeDocumentsTab;
